import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import type { GeoUnit } from '@/features/geo/types/geo.types';
import type { HierarchyFilterOption } from '@/features/leaveApplications/types/leaveApplication.types';
import { DISTRICT_FIRST_FIELD_STATE, getDateFromToFilterFields } from '@/utils/filterFields';
import {
  getSelectFieldsWhenOptionsExist,
  parseEnumFilter,
  parseNumberFilter,
  toFilterOptions,
  toFilterText,
  type OptionWithCount,
} from '@/utils/filters';
import {
  TEACHER_CATEGORY,
  TEACHER_CATEGORY_LABELS,
  TEACHER_STATUS,
  TEACHER_STATUS_LABELS,
  UPDATED_FROM_FILTER_KEY,
  UPDATED_TO_FILTER_KEY,
} from '../constants/teacherSubmission.constants';
import type {
  TeacherCountsBySubmitterParams,
  TeacherLookupItem,
  TeacherSubmissionSearch,
  TeacherSummaryParams,
} from '../types/teacherSubmission.types';

export type TeacherSubmissionFilters = Record<string, string>;

type TeacherSubmissionFilterSearch = Omit<TeacherSubmissionSearch, 'search'>;

const STATUS_OPTIONS: FilterOption[] = TEACHER_STATUS.map((status) => ({
  label: TEACHER_STATUS_LABELS[status],
  value: status,
}));

const CATEGORY_OPTIONS: FilterOption[] = TEACHER_CATEGORY.map((category) => ({
  label: TEACHER_CATEGORY_LABELS[category],
  value: category,
}));

export function toFilterValues(search: TeacherSubmissionSearch): TeacherSubmissionFilters {
  return {
    instituteId: toFilterText(search.instituteId),
    status: toFilterText(search.teacherStatus),
    districtId: toFilterText(search.districtId),
    thanaId: toFilterText(search.thanaId),
    designationId: toFilterText(search.designationId),
    category: toFilterText(search.category),
    amUserId: toFilterText(search.amUserId),
    updatedBy: toFilterText(search.updatedBy),
    updatedFrom: toFilterText(search.updatedFrom),
    updatedTo: toFilterText(search.updatedTo),
  };
}

export function toSearchFilters(filters: TeacherSubmissionFilters): TeacherSubmissionFilterSearch {
  return {
    instituteId: parseNumberFilter(filters.instituteId),
    teacherStatus: parseEnumFilter(filters.status, TEACHER_STATUS),
    districtId: parseNumberFilter(filters.districtId),
    thanaId: parseNumberFilter(filters.thanaId),
    designationId: parseNumberFilter(filters.designationId),
    category: parseEnumFilter(filters.category, TEACHER_CATEGORY),
    amUserId: parseNumberFilter(filters.amUserId),
    updatedBy: parseNumberFilter(filters.updatedBy),
    updatedFrom: filters.updatedFrom || undefined,
    updatedTo: filters.updatedTo || undefined,
  };
}

export function clearDependentFilters(
  previous: TeacherSubmissionFilters,
  next: TeacherSubmissionFilters,
): TeacherSubmissionFilters {
  const amChanged = next.amUserId !== previous.amUserId;
  const districtChanged = next.districtId !== previous.districtId;

  return {
    ...next,
    ...(amChanged ? { updatedBy: '' } : {}),
    ...(districtChanged ? { thanaId: '' } : {}),
  };
}

export function toSummaryParams(search: TeacherSubmissionSearch): TeacherSummaryParams {
  return {
    instituteId: search.instituteId,
    districtId: search.districtId,
    thanaId: search.thanaId,
    designationId: search.designationId,
    category: search.category,
    amUserId: search.amUserId,
    updatedBy: search.updatedBy,
    updatedFrom: search.updatedFrom,
    updatedTo: search.updatedTo,
    search: search.search,
  };
}

export function toCountsBySubmitterParams(
  search: TeacherSubmissionSearch,
): TeacherCountsBySubmitterParams {
  return {
    instituteId: search.instituteId,
    districtId: search.districtId,
    thanaId: search.thanaId,
    designationId: search.designationId,
    category: search.category,
    updatedFrom: search.updatedFrom,
    updatedTo: search.updatedTo,
    search: search.search,
    status: search.teacherStatus,
  };
}

interface GetTeacherSubmissionFilterFieldsArgs {
  selectedInstitute: FilterOption | null;
  hasDistrict: boolean;
  districts: GeoUnit[] | undefined;
  thanas: GeoUnit[] | undefined;
  designations: TeacherLookupItem[] | undefined;
  amOptions: OptionWithCount<HierarchyFilterOption>[] | undefined;
  submitterOptions: OptionWithCount<HierarchyFilterOption>[] | undefined;
}

export function getTeacherSubmissionFilterFields({
  selectedInstitute,
  hasDistrict,
  districts,
  thanas,
  designations,
  amOptions,
  submitterOptions,
}: GetTeacherSubmissionFilterFieldsArgs): FilterField[] {
  return [
    ...getDateFromToFilterFields({
      fromKey: UPDATED_FROM_FILTER_KEY,
      toKey: UPDATED_TO_FILTER_KEY,
    }),
    ...getInstituteFields(selectedInstitute),
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    {
      key: 'districtId',
      label: 'District',
      type: 'select',
      options: toFilterOptions(districts),
      searchable: true,
    },
    {
      key: 'thanaId',
      label: 'Thana',
      type: 'select',
      options: toFilterOptions(thanas),
      searchable: true,
      ...(hasDistrict ? {} : DISTRICT_FIRST_FIELD_STATE),
    },
    {
      key: 'designationId',
      label: 'Designation',
      type: 'select',
      options: toFilterOptions(designations),
      searchable: true,
    },
    { key: 'category', label: 'Category', type: 'select', options: CATEGORY_OPTIONS },
    ...getSelectFieldsWhenOptionsExist('amUserId', 'AM Name', amOptions),
    ...getSelectFieldsWhenOptionsExist('updatedBy', 'ZM Name', submitterOptions),
  ];
}

function getInstituteFields(selectedInstitute: FilterOption | null): FilterField[] {
  if (!selectedInstitute) return [];

  return [{ key: 'instituteId', label: 'Institute', type: 'select', options: [selectedInstitute] }];
}
